import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, ClipboardList, Clock, PackageCheck, Plus, ShoppingCart } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { FichaSolicitud } from "@/componentes/suministros/FichaSolicitud"
import { FormularioSolicitud } from "@/componentes/suministros/FormularioSolicitud"
import type { Solicitud } from "@/componentes/suministros/tipos"
import { ESTADOS } from "@/componentes/suministros/tipos"
import { comoCantidad } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/** El tono de cada estado. El texto va siempre escrito: el color sólo refuerza. */
const TONO: Record<Solicitud["estado"], Tono> = {
  BORRADOR: "neutro",
  EN_APROBACION: "aviso",
  APROBADA: "info",
  RECHAZADA: "peligro",
  ATENDIDA: "exito",
  ANULADA: "neutro",
}

/** Qué filtra cada tarjeta. `null` es «todas». */
type Filtro = Solicitud["estado"] | null

/**
 * Solicitudes de bienes y consumibles: lo que la gente de la casa pide.
 *
 * ⚠ **Quien sólo puede pedir ve ÚNICAMENTE lo suyo, y lo recorta el SERVIDOR.** Aquí no
 * se filtra por solicitante: esconder filas en el navegador es cosmética, y lo que llega
 * ya viene recortado por `authz.can()` según se tenga o no `suministro:ver`.
 *
 * ⚠ **Los indicadores se pulsan y filtran la tabla**, como en la bandeja de órdenes y en
 * «Gastos y categorías». Una cifra que no se puede abrir obliga a recorrer la lista a ojo
 * para saber qué la compone.
 *
 * ⚠ **Pedir no compromete dinero, y la pantalla no lo disimula.** No hay un solo importe
 * en esta vista: lo que cuesta aparece cuando alguien redacta la orden de compra, que es
 * donde empiezan las tres firmas.
 */
export function Solicitudes() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [filtro, setFiltro] = useState<Filtro>(null)
  const [creando, setCreando] = useState(false)
  const [abierta, setAbierta] = useState<number | null>(null)
  const vista = useRef<HTMLDivElement | null>(null)

  const puedeSolicitar = tienePermiso(usuario, "suministro:solicitar")

  const solicitudes = useQuery({
    queryKey: ["solicitudes-suministro"],
    queryFn: () => api.get<Solicitud[]>("/api/v1/solicitudes-suministro?limite=200"),
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["solicitudes-suministro"] })
    clienteQuery.invalidateQueries({ queryKey: ["articulos"] })
    clienteQuery.invalidateQueries({ queryKey: ["inventario-resumen"] })
  }

  const crear = useMutation({
    meta: { exito: { titulo: "Solicitud enviada", texto: "Queda en espera de aprobación." } },
    mutationFn: (cuerpo: unknown) => api.post("/api/v1/solicitudes-suministro", cuerpo),
    onSuccess: () => {
      setCreando(false)
      refrescar()
    },
  })

  if (solicitudes.isPending) return <p className="py-6">Cargando…</p>
  if (solicitudes.isError) {
    return (
      <p
        className="flex items-center gap-2 py-6"
        role="alert"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} /> No se pudieron cargar las solicitudes.
      </p>
    )
  }

  const filas = solicitudes.data
  const esperando = filas.filter((s) => s.estado === "EN_APROBACION").length
  const aprobadas = filas.filter((s) => s.estado === "APROBADA").length
  const atendidas = filas.filter((s) => s.estado === "ATENDIDA").length
  const alternar = (cual: Filtro) => setFiltro(filtro === cual ? null : cual)
  const visibles = filtro === null ? filas : filas.filter((s) => s.estado === filtro)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-end gap-2">
        <Exportar
          titulo="Solicitudes de suministros"
          subtitulo={filtro === null ? "Todas las que puedes ver" : `Sólo ${ESTADOS[filtro]}`}
          contenido={vista}
        />
        {puedeSolicitar && (
          <Boton variante="primario" onClick={() => setCreando(true)}>
            <Plus size={16} /> Pedir algo
          </Boton>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo="Pedidos"
          detalle="Todo lo que se ha pedido y puedes ver"
          Icono={ClipboardList}
          color="var(--info)"
          cantidad={filas.length}
          pie={filtro === null ? "Sin filtro" : "Pulsa para quitar el filtro"}
          activa={filtro === null && false}
          onClick={() => setFiltro(null)}
        />
        <TarjetaKpi
          titulo="Esperando decisión"
          detalle="Pedidas y sin aprobar ni rechazar"
          Icono={Clock}
          color="var(--warning)"
          cantidad={esperando}
          pie={esperando > 0 ? "Pulsa para ver cuáles son" : "Nada esperando a nadie"}
          activa={filtro === "EN_APROBACION"}
          onClick={esperando > 0 ? () => alternar("EN_APROBACION") : undefined}
        />
        <TarjetaKpi
          titulo="Aprobadas por servir"
          detalle="Autorizadas: hay que entregarlas o comprarlas"
          Icono={ShoppingCart}
          color="var(--primary)"
          cantidad={aprobadas}
          pie={aprobadas > 0 ? "Pulsa para ver cuáles son" : "Nada pendiente de servir"}
          activa={filtro === "APROBADA"}
          onClick={aprobadas > 0 ? () => alternar("APROBADA") : undefined}
        />
        <TarjetaKpi
          titulo="Atendidas"
          detalle="Ya entregadas a quien las pidió"
          Icono={PackageCheck}
          color="var(--success)"
          cantidad={atendidas}
          pie={atendidas > 0 ? "Pulsa para ver cuáles son" : "Todavía ninguna"}
          activa={filtro === "ATENDIDA"}
          onClick={atendidas > 0 ? () => alternar("ATENDIDA") : undefined}
        />
      </div>

      {filtro !== null && (
        <p className="text-sm">
          Viendo {ESTADOS[filtro]}.{" "}
          <button
            type="button"
            onClick={() => setFiltro(null)}
            className="foco-cristal underline-offset-2 hover:underline"
            style={{ color: "var(--primary-texto)" }}
          >
            Quitar el filtro
          </button>
        </p>
      )}

      <div ref={vista}>
        <Tabla etiqueta="Solicitudes de bienes y consumibles">
          <Cabecera>
            <Th>Código</Th>
            <Th>Quién pidió</Th>
            <Th>Qué pidió</Th>
            <Th>Estado</Th>
            <Th>Para cuándo</Th>
          </Cabecera>
          <Cuerpo>
            {visibles.length === 0 && (
              <TablaVacia colSpan={5}>
                {filtro === null
                  ? "Todavía no hay solicitudes. La primera se hace con «Pedir algo»."
                  : "Ninguna en ese estado. El filtro está puesto, no es que no haya nada."}
              </TablaVacia>
            )}
            {visibles.map((s) => (
              <Fila key={s.id} onClick={() => setAbierta(s.id)}>
                <Td className="tabular-nums">{s.codigo}</Td>
                <Td>{s.solicitante ?? "—"}</Td>
                <Td
                  // En el archivo, todos los renglones con su cantidad, no «y 2 más».
                  data-exportar-valor={
                    s.lineas
                      .map((l) => `${comoCantidad(l.cantidad)} ${l.descripcion}`)
                      .join("; ") || "—"
                  }
                >
                  {s.lineas[0]?.descripcion ?? "—"}
                  {s.lineas.length > 1 && (
                    <span style={{ color: "var(--fg-muted)" }}> y {s.lineas.length - 1} más</span>
                  )}
                </Td>
                <Td>
                  <Insignia tono={TONO[s.estado]}>{ESTADOS[s.estado]}</Insignia>
                  {s.prioridad === "URGENTE" && (
                    <>
                      {" "}
                      <Insignia tono="peligro">Urgente</Insignia>
                    </>
                  )}
                </Td>
                <Td
                  className="tabular-nums"
                  data-exportar-valor={s.necesaria_para ? comoFecha(s.necesaria_para) : "—"}
                >
                  {s.necesaria_para ?? "—"}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      {creando && (
        <Modal
          abierto={true}
          onCerrar={() => setCreando(false)}
          titulo="Pedir bienes o consumibles"
        >
          <FormularioSolicitud
            onGuardar={(cuerpo) => crear.mutate(cuerpo)}
            guardando={crear.isPending}
            error={crear.error}
          />
        </Modal>
      )}

      {abierta !== null && (
        <FichaSolicitud
          solicitudId={abierta}
          onCerrar={() => setAbierta(null)}
          onCambio={refrescar}
        />
      )}
    </div>
  )
}
